import { CancelToken } from 'axios'
import { useContext } from './context'
import { useSelectedTrip, useTripSummary } from './trip'
import { useMutation } from './vue-query'
import { LocationDataSource, WaypointDataSource } from '@/utils/enums'
import { Location, LocationAutocompleteResult } from '@/types/trip'
import GoogleAPIManager from '@/utils/google-api-manager'

export interface LocationDetailsParams {
  locationResult: LocationAutocompleteResult
  cancelToken?: CancelToken
}

/**
 * Custom composable hook for fetching detailed location information
 *
 * This hook provides a mutation function that retrieves location details from various sources:
 * 1. Returns location directly if data source is MAPBOX
 * 2. Searches existing trip waypoints for already fetched location data
 * 3. Searches existing trip accommodation for already fetched location data
 * 4. Fetches fresh location details from the API as a fallback
 */
export function useLocationDetails() {
  const { $axios } = useContext()
  const trip = useSelectedTrip()

  const { data: tripSummary } = useTripSummary(trip.value._id)

  return useMutation({
    mutationFn: async (params: LocationDetailsParams) => {
      const { locationResult, cancelToken } = params

      // MAPBOX locations already contain complete data, no need to fetch details
      if (locationResult.dataSource === LocationDataSource.MAPBOX) {
        return locationResult.location
      }

      const placeId = locationResult.location.placeId

      // First, check if location details already exist in trip waypoints to avoid unnecessary API calls
      let locationDetails = trip.value.waypoints.find(
        (waypoints) => waypoints.location.placeId === placeId,
      )?.location
      if (locationDetails) {
        return locationDetails
      }

      // Second, check if location details exist in trip accommodation data
      locationDetails = tripSummary.value?.accommodation.find(
        (accommodation) => accommodation.location?.placeId === placeId,
      )?.location
      if (locationDetails) {
        return locationDetails
      }

      // Last resort: fetch fresh location details from the API
      const {
        data: {
          data: { location },
        },
      } = await $axios.get<{ data: { location: Location } }>(
        `/api/meta/locations/search/details`,
        {
          params: {
            ds: WaypointDataSource.GOOGLE,
            place_id: placeId,
            token: GoogleAPIManager.generateSessionToken(),
          },
          cancelToken,
        },
      )

      return location
    },
  })
}
